import React from 'react'
// libs
import { useDispatch } from 'react-redux'
import { useMutation } from '@apollo/client/react'
import { DragDropContext, Draggable, Droppable, DropResult } from 'react-beautiful-dnd'
// hooks
import { useCurrentUser, useModalData } from 'hooks/index'
// components
import { ColorSelect } from 'components/FormElements'
import { CloseIcon, TrashIcon } from 'components/UI/Icons'
import { Button, BUTTON_LAYOUTS, Modal } from 'components/UI'
// constants
import { Modals } from 'constants/modals'
// graphql
import { COMPANY_RISK_RESPONSE_TYPES_QUERY } from 'graphql/company/queries'
// types
import { IResponseTypesSet, IRiskResponseTypeOption } from 'types/graphql'
// store
import { setResponseTypes } from 'store/reducers/riskTemplates'
// graphql
import { COMPANY_UPDATE_RISK_RESPONSE_MUTATION } from 'graphql/company/mutations'
// views
import { colors } from 'themes/index'
import { AddButton, ButtonsContainer, Container, DragIcon, Header, Option, Scrollbar, Value } from './views'
// utils
import { get } from 'lodash'
import { v4 as uuid } from 'uuid'
import { handleError } from 'utils/errors/handlers'

interface Props {
  responseTypeSets: IResponseTypesSet[]
}

const ResponseTypesSetModal: React.FC<Props> = ({ responseTypeSets }) => {
  const user = useCurrentUser()
  const dispatch = useDispatch()

  const [updateRiskResponse] = useMutation(COMPANY_UPDATE_RISK_RESPONSE_MUTATION, {
    refetchQueries: [COMPANY_RISK_RESPONSE_TYPES_QUERY]
  })

  const [options, setOptions] = React.useState<IRiskResponseTypeOption[]>([])

  const {
    hideModal,
    modalData: { isOpen = false, data }
  } = useModalData({ modalType: Modals.RESPONSE_TYPES_SET_MODAL })

  const reorderOptions = (list: IRiskResponseTypeOption[], startIndex: number, endIndex: number) => {
    const reorderedList = [...list]

    const [removed] = reorderedList.splice(startIndex, 1)
    reorderedList.splice(endIndex, 0, removed)
    return reorderedList
  }

  const onDragEnd = ({ source, destination }: DropResult) => {
    if (!destination) {
      return
    }

    if (source.droppableId === destination.droppableId) {
      setOptions((prev) => reorderOptions(prev, source.index, destination.index))
    }
  }

  const onSaveClick = async () => {
    try {
      if (data?.id) {
        const input: IResponseTypesSet[] = responseTypeSets.map((item) => {
          if (item.id === data.id) {
            return { ...data, options }
          }

          return item
        })

        await updateRiskResponse({
          variables: {
            id: user.company.id,
            input: {
              responseTypesSets: input
            }
          }
        })

        dispatch(setResponseTypes(input))
      } else {
        const input = [...responseTypeSets, { id: uuid(), options }]

        await updateRiskResponse({
          variables: {
            id: user.company.id,
            input: {
              responseTypesSets: input
            }
          }
        })

        dispatch(setResponseTypes(input))
      }

      hideModal()
    } catch (e) {
      handleError(e, 'ResponseTypesSetModal.onSaveClick')
    }
  }

  const createOption = () =>
    setOptions((prev) => [
      ...prev,
      {
        id: uuid(),
        value: null,
        label: null,
        score: null,
        color: colors.gray250,
        active: true
      }
    ])

  const deleteOption = (e: React.MouseEvent<SVGSVGElement>, idx: number) => {
    e.stopPropagation()

    setOptions((prev) => {
      prev.splice(idx, 1)
      return [...prev]
    })
  }

  const onColorChange = (idx: number) => (color: string) =>
    setOptions((prev) =>
      prev.map((item, i) => {
        if (i === idx) {
          return { ...item, color }
        }
        return item
      })
    )

  const onInputBlur = (e: React.FocusEvent<HTMLInputElement>, idx: number, key: string) => {
    const value = get(e.target, 'value', null)

    setOptions((prev) =>
      prev.map((item, i) => {
        if (idx === i) {
          return { ...item, [key]: value }
        }
        return item
      })
    )
  }

  React.useEffect(() => {
    if (isOpen && data?.options) {
      setOptions(data.options)
    }

    if (!isOpen && options.length) {
      setOptions([])
    }
  }, [isOpen, data?.options])

  return (
    <Modal isOpen={isOpen} width='md' hideModal={hideModal}>
      <Container data-testid='response-types-set-modal'>
        <Header>
          <span>Label</span> <span>Value</span>
          <span>Score</span>
          <span>Color</span>
          <CloseIcon onClick={hideModal} />
        </Header>
        <Scrollbar>
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId={'MULTIPLE_RESPONSES_OPTIONS'}>
              {(provided) => (
                <div ref={provided.innerRef}>
                  {options.map((option, idx) => (
                    <Draggable key={option.id} draggableId={option.id} index={idx} isDragDisabled={false}>
                      {(provided) => (
                        <Option
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          style={provided.draggableProps.style as React.CSSProperties}>
                          <Value>
                            <span {...provided.dragHandleProps}>
                              <DragIcon />
                            </span>
                            <input
                              type='text'
                              placeholder='set'
                              data-testid='response-types-set-option-label'
                              defaultValue={option.label}
                              onBlur={(e) => onInputBlur(e, idx, 'label')}
                            />
                          </Value>
                          <Value>
                            <input
                              type='text'
                              placeholder='set'
                              data-testid='response-types-set-option-value'
                              defaultValue={option.value}
                              onBlur={(e) => onInputBlur(e, idx, 'value')}
                            />
                          </Value>
                          <Value>
                            <input
                              type='number'
                              placeholder='set'
                              defaultValue={option.score}
                              onBlur={(e) => onInputBlur(e, idx, 'score')}
                            />
                          </Value>
                          <ColorSelect
                            initialColor={option.color}
                            onChange={onColorChange(idx)}
                            trigger={<Value color={option.color}>{option.color || 'set'}</Value>}
                          />
                          <TrashIcon onClick={(e) => deleteOption(e, idx)} />
                        </Option>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </Scrollbar>

        <AddButton data-testid='response-types-set-add-option' onClick={createOption}>
          + add option
        </AddButton>
        <ButtonsContainer>
          <Button shadowLess type='button' onClick={onSaveClick} layout={BUTTON_LAYOUTS.PURPLE}>
            Save
          </Button>
          <Button shadowLess layout={BUTTON_LAYOUTS.PURPLE_INVERTED} onClick={hideModal}>
            Cancel
          </Button>
        </ButtonsContainer>
      </Container>
    </Modal>
  )
}

export default ResponseTypesSetModal
